import { Field, IconButton, Switch, TextInput, toast } from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { cancelTrainingGpuQuotaRequest, resolveTrainingGpuQuotaRequest } from "@/api";
import { ResourceBrowserLayout } from "@/components/browse";
import { FilterBar, FilterResultCount, type FilterBarChip } from "@/components/filter";
import { HatTextModal } from "@/components/modal";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { isHttpClientError } from "@/http";
import { createGpuQuotaRequestColumns } from "./gpuQuotaRequestColumns";
import {
  EMPTY_GPU_QUOTA_REQUEST_FILTERS,
  gpuQuotaRequestFilterChips,
  readGpuQuotaRequestFilters,
  writeGpuQuotaRequestFilters,
  type GpuQuotaRequestFilterState,
} from "./gpuQuotaRequestFilters";
import { GpuQuotaRequestTableSource } from "./GpuQuotaRequestTableSource";
import type { GpuQuotaRequestTableRow } from "./GpuQuotaRequestTableRow";
import { tableStripError } from "@/components/table/tableStateMessages";

// 종결 메모의 길이 상한. 서버 계약과 같다.
const resolutionNoteMaxLength = 1000;

// 확인이 필요한 행 동작. 철회와 종결은 이력이므로 되돌릴 수 없다. 그래서 한 번 더 묻는다.
type PendingAction =
  | { kind: "cancel"; row: GpuQuotaRequestTableRow }
  | { kind: "resolve"; row: GpuQuotaRequestTableRow; status: "applied" | "rejected" };

// 요청 탭. 조직의 요청 이력을 표시한다. 같은 조직이면 팀이 달라도, 내 요청이 아니어도 모두
// 보인다(누가 요청했는지도 보인다). 좁히기는 서버 파라미터로만 한다. FE 는 받은 목록을 다시
// 거르지 않는다.
//
// 요청자와 선점자는 다르다. 좁히기와 철회 권한은 행에 기록된 접수자(requested_by)가 기준이다.
// 표에 함께 보이는 owner_member 는 그 workspace 를 지금 가진 사람이므로 재매핑으로 바뀐다.
//
// 행 동작 표시는 서버가 준 canCancel/canResolve 만 본다. FE 가 등급과 소유를 다시 판정하면
// 서버 규칙과 달라진다.
//
// 조회 축(대기만, 내 요청만)은 URL 이 소유한다(rules.md §17). 이 축들은 이 탭에만 적용되므로
// 필터 바는 페이지 belowTabs 가 아니라 이 패널의 ResourceBrowserLayout filters 슬롯에 둔다.
export function GpuQuotaRequestPanel({
  // 내 요청 필터에 보낼 값. null 이면(세션 복구 전 등) 토글을 그리지 않는다.
  // 표시명 비교로 "내 요청인지" 추측하면 이름이 같은 다른 사람을 구분하지 못한다.
  myMemberUuid,
}: {
  myMemberUuid: string | null;
}): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readGpuQuotaRequestFilters(searchParams), [searchParams]);
  // 내 member 를 모르면 "내 요청만" 에 보낼 값이 없다. URL 에 값이 남아 있어도 전체를 조회한다.
  const mineOnly = filters.mineOnly && myMemberUuid !== null;
  const requestedByMemberUuid = mineOnly ? (myMemberUuid ?? undefined) : undefined;
  const { pendingOnly } = filters;

  const [source] = useState(
    () =>
      new GpuQuotaRequestTableSource({
        initialPendingOnly: pendingOnly,
        initialRequestedByMemberUuid: requestedByMemberUuid,
      }),
  );
  const [action, setAction] = useState<PendingAction | null>(null);
  // 종결(승인·거절)에 붙이는 메모. 확인 창을 열 때마다 비운다.
  const [resolutionNote, setResolutionNote] = useState("");
  const [busy, setBusy] = useState(false);

  const openAction = useCallback((next: PendingAction) => {
    setResolutionNote("");
    setAction(next);
  }, []);

  // 언마운트 뒤 도착한 응답이 상태를 바꾸지 않게 하는 가드. StrictMode 재마운트에서 다시 켠다.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    void source.initialize();
    return () => {
      source.dispose();
    };
  }, [source]);

  // URL 조건이 바뀌면(스위치, 칩, 뒤로가기) 소스에 전달한다. setter 는 같은 값이면 no-op 이다.
  // 그래서 첫 마운트에서는 다시 조회하지 않는다. 생성 옵션이 이미 그 값을 가진다.
  useEffect(() => {
    void source.setPendingOnly(pendingOnly);
  }, [source, pendingOnly]);
  useEffect(() => {
    void source.setRequestedByMemberUuid(requestedByMemberUuid);
  }, [source, requestedByMemberUuid]);

  const handleFiltersChange = useCallback(
    (next: GpuQuotaRequestFilterState) => {
      setSearchParams((prev) => writeGpuQuotaRequestFilters(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const isLoading = snapshot.loadState === "loading";

  const columns = useMemo(
    () =>
      createGpuQuotaRequestColumns({
        onCancel: (row) => openAction({ kind: "cancel", row }),
        onResolve: (row, status) => openAction({ kind: "resolve", row, status }),
      }),
    [openAction],
  );

  const handleConfirm = (): void => {
    if (action === null || busy) {
      return;
    }
    setBusy(true);
    // 빈 메모는 보내지 않는다. 서버는 메모 없음과 빈 문자열을 다르게 저장한다.
    const note = resolutionNote.trim();
    const request =
      action.kind === "cancel"
        ? cancelTrainingGpuQuotaRequest(action.row.requestUuid)
        : resolveTrainingGpuQuotaRequest(action.row.requestUuid, {
            status: action.status,
            ...(note === "" ? {} : { resolutionNote: note }),
          });
    void request
      .then(() => {
        toast.success(actionSuccessMessage(action));
      })
      .catch((error: unknown) => {
        toast.error(resolveActionErrorMessage(error));
      })
      .finally(() => {
        // 성공과 실패 모두 목록을 다시 읽는다. 409 는 서버 상태가 이미 바뀌었다는 뜻이다.
        void source.refresh();
        if (aliveRef.current) {
          setBusy(false);
          setAction(null);
        }
      });
  };

  // 칩은 실제로 적용된 조건만 표시한다. 내 member 를 모르면 mine 은 적용되지 않은 것이다.
  const effectiveFilters: GpuQuotaRequestFilterState = { pendingOnly, mineOnly };
  const chips: FilterBarChip[] = gpuQuotaRequestFilterChips(effectiveFilters).map((chip) => ({
    key: chip.axis,
    label: chip.label,
    removeLabel: `${chip.label} 해제`,
    onRemove: () => handleFiltersChange({ ...effectiveFilters, [chip.axis]: false }),
  }));
  const summaryLabel = pendingOnly ? "대기 중인 요청" : "요청";

  return (
    <>
      <ResourceBrowserLayout
        ariaLabel="GPU 한도 요청 목록"
        summaryCount={snapshot.total}
        summaryLabel={summaryLabel}
        summaryInFilter
        filters={
          // 두 축 모두 2값 포함 축이므로 Field+Switch 를 쓴다(rules.md §17). 검색 축은 없다.
          <FilterBar
            ariaLabel="한도 요청 필터"
            chips={chips}
            onClearAll={() => handleFiltersChange(EMPTY_GPU_QUOTA_REQUEST_FILTERS)}
          >
            <Field label="대기만" layout="hug" labelPlacement="end">
              <Switch
                checked={pendingOnly}
                aria-label="대기만"
                onChange={(event) =>
                  handleFiltersChange({ ...effectiveFilters, pendingOnly: event.target.checked })
                }
              />
            </Field>
            {myMemberUuid === null ? null : (
              <Field label="내 요청만" layout="hug" labelPlacement="end">
                <Switch
                  checked={mineOnly}
                  aria-label="내 요청만"
                  onChange={(event) =>
                    handleFiltersChange({ ...effectiveFilters, mineOnly: event.target.checked })
                  }
                />
              </Field>
            )}
          </FilterBar>
        }
        error={tableStripError(snapshot)}
        onRetry={() => void source.refresh()}
      >
        {/* 목록 상단 띠. 결과 수(idle)와 새로고침을 표시한다. 행 선택이 없는 목록이라 액션은 없다. */}
        <div className={bulkActionBarStyles.host}>
          <BulkActionBar
            source={source}
            idleContent={<FilterResultCount count={snapshot.total} unitLabel={summaryLabel} />}
            rightContent={
              <IconButton
                text={isLoading ? "불러오는 중…" : "새로고침"}
                size="sm"
                variant="surface"
                disabled={isLoading}
                onClick={() => void source.refresh()}
              />
            }
            ariaLabel="한도 요청 목록 도구"
          />
          <HatDataTable
            source={source}
            columns={columns}
            emptyMessage={pendingOnly ? "대기 중인 요청이 없습니다." : "아직 한도 요청이 없습니다."}
            loadingMessage="한도 요청을 불러오고 있습니다."
            ariaLabel="GPU 한도 요청 테이블"
            showIndex={false}
            scrollable
            minWidth="1260px"
          />
        </div>
      </ResourceBrowserLayout>
      <HatTextModal
        open={action !== null}
        label={action === null ? "" : actionLabel(action)}
        eyebrow={action?.row.workspaceName}
        caption={action === null ? undefined : actionCaption(action)}
        okText={action === null ? "확인" : actionOkText(action)}
        cancelText="취소"
        busy={busy}
        onOkPress={handleConfirm}
        onCancelPress={() => setAction(null)}
        content={
          // 철회는 요청자가 스스로 거두는 동작이라 메모가 없다. 종결만 메모를 받는다.
          action?.kind === "resolve" ? (
            <Field label={resolutionNoteLabel(action.status)}>
              <TextInput
                value={resolutionNote}
                maxLength={resolutionNoteMaxLength}
                placeholder={resolutionNotePlaceholder(action.status)}
                onChange={(event) => setResolutionNote(event.target.value)}
              />
            </Field>
          ) : undefined
        }
      />
    </>
  );
}

function actionLabel(action: PendingAction): string {
  if (action.kind === "cancel") {
    return "이 요청을 철회합니다.";
  }
  return action.status === "applied" ? "이 요청을 승인합니다." : "이 요청을 거절합니다.";
}

function actionCaption(action: PendingAction): string {
  if (action.kind === "cancel") {
    return "철회한 요청은 되돌릴 수 없습니다. 다시 필요하면 새로 요청해 주세요.";
  }
  if (action.status === "applied") {
    // 서버는 대기 중인 요청에 항상 기간을 보낸다. 값이 없으면 기간 표현만 뺀다.
    const period =
      action.row.requestedDays === null
        ? "지금부터"
        : `지금부터 ${action.row.requestedDays}일 동안`;
    return `승인하면 ${period} 한도가 ${action.row.requestedGpuLimit} 로 올라갑니다. 기간이 끝나면 원래 한도로 돌아갑니다.`;
  }
  return "거절한 요청은 되돌릴 수 없습니다. 요청자가 사유를 볼 수 있습니다.";
}

// 메모는 선택이다. 거절 사유는 요청자가 다음 행동을 정하는 근거라서 적기를 권한다.
function resolutionNoteLabel(status: "applied" | "rejected"): string {
  return status === "rejected" ? "거절 사유 (선택)" : "메모 (선택)";
}

function resolutionNotePlaceholder(status: "applied" | "rejected"): string {
  return status === "rejected"
    ? "예) 이번 주는 여유 GPU 가 없습니다"
    : "예) 다음 주 실험 일정을 확인했습니다";
}

function actionOkText(action: PendingAction): string {
  if (action.kind === "cancel") {
    return "철회";
  }
  return action.status === "applied" ? "승인" : "거절";
}

function actionSuccessMessage(action: PendingAction): string {
  if (action.kind === "cancel") {
    return "요청을 철회했습니다.";
  }
  return action.status === "applied"
    ? "요청을 승인했습니다. 한도가 올라갔습니다."
    : "요청을 거절했습니다.";
}

/** 행 동작 실패 문구. 계약된 코드는 원인을 구체적으로 안내한다. */
function resolveActionErrorMessage(error: unknown): string {
  if (isHttpClientError(error)) {
    switch (error.code) {
      case "quota_request.invalid_status":
        return "이미 종결된 요청입니다. 목록을 새로 읽었습니다.";
      // 같은 요청의 승인·거절·철회가 이미 실행 중이다. 그 동작이 끝나면 요청은 종결돼 있다.
      case "quota_request.processing":
        return "다른 곳에서 이 요청을 처리하고 있습니다. 잠시 뒤 다시 시도해 주세요.";
      case "quota_request.forbidden":
        return "이 요청을 처리할 권한이 없습니다.";
      case "quota_request.not_found":
        return "요청을 찾을 수 없습니다. 목록을 새로 읽었습니다.";
      case "upstream.unavailable":
        return "training-infra 가 응답하지 않습니다. 요청은 대기 상태로 남았습니다. 잠시 뒤 다시 시도해 주세요.";
    }
    // 승인·거절·철회는 training-infra 를 먼저 부른다. 그 호출이 거절되면 요청은 바뀌지 않는다.
    if (error.code?.startsWith("upstream.") === true) {
      return "training-infra 가 요청을 처리하지 못했습니다. 요청은 대기 상태로 남았습니다.";
    }
  }
  return "요청을 처리하지 못했습니다.";
}
